import React, { useState } from "react"

import VideoPreview from "../../components/VideoPreview"
import { useToast } from "../../contexts/ToastContext"

import { VideoReviewStatus } from "./VideoReviewStatus"

const VideoModal = ({ videoUrl, onClose }) => {
  if (!videoUrl) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-[0.75]" onClick={onClose}>
      <div
        className="relative max-h-screen w-full max-w-4xl rounded-lg bg-white p-4 shadow-lg"
        onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute -right-3 -top-3 flex h-8 w-8 items-center justify-center rounded-full bg-gray-dark text-white transition-colors hover:bg-gray-600">
          <i className="icon-close" />
        </button>
        <video src={videoUrl} controls autoPlay className="w-full rounded" style={{ maxHeight: "90vh" }} />
      </div>
    </div>
  )
}

const VideoReviewsTable = ({ videoReviews, onApprove, onReject, loading }) => {
  const { showToast } = useToast()
  const [selectedVideoUrl, setSelectedVideoUrl] = useState(null)
  const [checkingStatus, setCheckingStatus] = useState(false)

  const handlePlayVideo = (videoUrl) => {
    setSelectedVideoUrl(videoUrl)
  }

  const handleCloseModal = () => {
    setSelectedVideoUrl(null)
  }

  const handleCheckStatus = async (videoId) => {
    setCheckingStatus(true)
    try {
      const response = await fetch(`/api/videos/${videoId}/job_status`, {
        method: "GET",
        headers: {
          Accept: "application/json"
        }
      })

      const data = await response.json()

      if (response.ok && data.success) {
        // Reload the page to show updated status
        if (typeof window !== "undefined") {
          window.location.reload()
        }
      } else {
        const errorMessage = data.error || "Error checking status"
        if (typeof window !== "undefined") {
          alert(errorMessage)
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error checking status:", error)
      if (typeof window !== "undefined") {
        alert("Network error while checking status")
      }
    } finally {
      setCheckingStatus(false)
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case VideoReviewStatus.PENDING:
        return "bg-orange text-white"
      case VideoReviewStatus.UPLOADING:
        return "bg-blue text-white"
      case VideoReviewStatus.PROCESSING:
        return "bg-blue text-white"
      case VideoReviewStatus.COMPLETED:
        return "bg-green text-white"
      case VideoReviewStatus.APPROVED:
        return "bg-green text-white"
      case VideoReviewStatus.REJECTED:
        return "bg-red text-white"
      case VideoReviewStatus.FAILED:
        return "bg-red text-white"
      default:
        return "bg-gray text-white"
    }
  }

  const getStatusDescription = (status) => {
    switch (status) {
      case VideoReviewStatus.PENDING:
        return "Waiting for processing"
      case VideoReviewStatus.UPLOADING:
        return "Uploading to S3"
      case VideoReviewStatus.PROCESSING:
        return "In MediaConvert"
      case VideoReviewStatus.COMPLETED:
        return "Ready for review"
      case VideoReviewStatus.APPROVED:
        return "Approved and visible"
      case VideoReviewStatus.REJECTED:
        return "Rejected"
      case VideoReviewStatus.FAILED:
        return "Processing error"
      default:
        return "Unknown status"
    }
  }

  const getTableRowClass = (status) => {
    if (status === VideoReviewStatus.FAILED) return "table-danger"
    if (status === VideoReviewStatus.APPROVED) return "table-success"
    return "table-light"
  }

  const formatDate = (dateString) => {
    if (!dateString) return "-"
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    })
  }

  const isApprovable = (status) => status === VideoReviewStatus.COMPLETED

  const isPendingUpload = (video) => {
    if (!video) return false
    const originalFilename = video.original_filename || video.originalFilename
    const uploadHash = video.upload_hash || video.uploadHash
    return video.status === VideoReviewStatus.PENDING && originalFilename === "pending_upload" && !!uploadHash
  }

  // const canExtendUpload = (video) =>
  //   isPendingUpload(video) &&
  //   video.upload_enabled &&
  //   new Date(video.upload_expires_at || video.uploadExpiresAt) > new Date()

  const formatExpiryDate = (dateString) => {
    if (!dateString) return "-"
    const date = new Date(dateString)
    const now = new Date()
    const isExpired = date < now

    const formatted = date.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    })

    return isExpired ? `${formatted} (Expired)` : formatted
  }

  const copyUploadLink = (hash) => {
    const link = `${window.location.origin}/video-review/upload/${hash}`
    navigator.clipboard
      .writeText(link)
      .then(() => {
        showToast({ content: "Upload link copied to clipboard!", type: "success" })
      })
      .catch(() => {
        prompt("Copy this upload link:", link)
      })
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-gray-light bg-gray-ultralight">
            <th className="p-4 text-left font-semibold text-gray-dark">ID</th>
            <th className="p-4 text-left font-semibold text-gray-dark">Client</th>
            <th className="p-4 text-left font-semibold text-gray-dark">Video</th>
            <th className="p-4 text-left font-semibold text-gray-dark">Status</th>
            <th className="p-4 text-left font-semibold text-gray-dark">Upload Link</th>
            <th className="p-4 text-left font-semibold text-gray-dark">Uploaded</th>
            <th className="p-4 text-left font-semibold text-gray-dark">Processed</th>
            <th className="p-4 text-left font-semibold text-gray-dark">Actions</th>
          </tr>
        </thead>
        <tbody>
          {videoReviews.map((video) => (
            <tr
              key={video.id}
              className={`border-b border-gray-light hover:bg-gray-ultralight ${getTableRowClass(video.status)}`}>
              <td className="p-4">{video.id}</td>
              <td className="p-4">
                {video.client?.first_name} {video.client?.last_name}
              </td>
              <td className="p-4">
                <div className="flex items-center">
                  <div className="mr-4">
                    <VideoPreview videoReview={video} className="h-[120px] w-[80px]" />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-dark">{video.original_filename}</div>
                    <div className="text-sm text-gray">{video.content_type || video.contentType}</div>
                    {video.title && <div className="mt-1 text-sm text-teal">{video.title}</div>}
                  </div>
                </div>
              </td>
              <td className="p-4">
                <span
                  className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${getStatusColor(
                    video.status
                  )}`}>
                  {video.status?.charAt(0).toUpperCase() + video.status?.slice(1)}
                </span>
                <div className="mt-1 text-sm text-gray-400">{getStatusDescription(video.status)}</div>
              </td>
              <td className="p-4">
                {isPendingUpload(video) ? (
                  <div>
                    <div className="mb-2 flex items-center space-x-2">
                      <button
                        onClick={() => copyUploadLink(video.upload_hash || video.uploadHash)}
                        className="inline-flex items-center rounded-full bg-teal px-3 py-1 text-xs text-white transition-colors hover:bg-teal-dark"
                        title="Copy Upload Link">
                        <i className="icon-link mr-1" />
                        Copy Link
                      </button>
                      {video.upload_enabled || video.uploadEnabled ? (
                        <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-1 text-xs text-green-800">
                          <i className="icon-done mr-1 text-xs" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-800">
                          <i className="icon-close mr-1 text-xs" />
                          Disabled
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray">
                      Expires: {formatExpiryDate(video.upload_expires_at || video.uploadExpiresAt)}
                    </div>
                  </div>
                ) : (
                  <span className="text-sm text-gray-500">No upload link</span>
                )}
              </td>
              <td className="p-4">
                <div>{formatDate(video.uploaded_at || video.uploadedAt)}</div>
                <div className="text-sm text-gray-500">by {video.user?.name || "System"}</div>
              </td>
              <td className="p-4">
                {video.processed_at || video.processedAt ? (
                  <>
                    <div>{formatDate(video.processed_at || video.processedAt)}</div>
                    {(video.processing_time || video.processingTime) && (
                      <div className="text-sm text-gray">
                        {(video.processing_time || video.processingTime).toFixed(1)}s
                      </div>
                    )}
                  </>
                ) : (
                  <span className="text-gray">-</span>
                )}
              </td>
              <td className="p-4">
                <div className="flex items-center space-x-2">
                  {(video.video_url || video.videoUrl) && (
                    <button
                      onClick={() => handlePlayVideo(video.video_url || video.videoUrl)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded border border-teal text-teal transition-colors hover:bg-teal hover:text-white"
                      title="View Video">
                      <i className="icon-play text-sm" />
                    </button>
                  )}
                  {isApprovable(video.status) && (
                    <>
                      <button
                        onClick={() => onApprove(video.id)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded border border-green text-green transition-colors hover:bg-green hover:text-white disabled:opacity-50"
                        title="Approve"
                        disabled={loading}>
                        <i className="icon-done text-sm" />
                      </button>
                      <button
                        onClick={() => onReject(video.id)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded border border-red text-red transition-colors hover:bg-red hover:text-white disabled:opacity-50"
                        title="Reject"
                        disabled={loading}>
                        <i className="icon-close text-sm" />
                      </button>
                    </>
                  )}

                  {/* Check Status Button for Processing Videos */}
                  {video.status === "processing" && (
                    <button
                      onClick={() => handleCheckStatus(video.id)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded border border-blue text-blue transition-colors hover:bg-blue hover:text-white disabled:opacity-50"
                      title="Check Processing Status"
                      disabled={checkingStatus}>
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                    </button>
                  )}

                  {/* Upload Link Management Actions */}
                  {isPendingUpload(video) && (
                    <>
                      <form
                        method="patch"
                        action={`/portal/video_reviews/${video.id}/extend_upload`}
                        className="inline">
                        <input
                          type="hidden"
                          name="authenticity_token"
                          value={
                            typeof document !== "undefined"
                              ? document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
                              : ""
                          }
                        />
                        <button
                          type="submit"
                          className="inline-flex h-8 w-8 items-center justify-center rounded border border-blue text-blue transition-colors hover:bg-blue hover:text-white"
                          title="Extend Upload Link by 24h">
                          <i className="icon-time text-sm" />
                        </button>
                      </form>

                      <form
                        method="patch"
                        action={`/portal/video_reviews/${video.id}/disable_upload`}
                        className="inline">
                        <input
                          type="hidden"
                          name="authenticity_token"
                          value={
                            typeof document !== "undefined"
                              ? document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
                              : ""
                          }
                        />
                        <button
                          type="submit"
                          className="inline-flex h-8 w-8 items-center justify-center rounded border border-orange text-orange transition-colors hover:bg-orange hover:text-white"
                          title="Disable Upload Link">
                          <i className="icon-block text-sm" />
                        </button>
                      </form>
                    </>
                  )}
                </div>
                {video.approved_by && (
                  <div className="mt-2 text-sm text-gray">
                    <div>by {video.approved_by.name}</div>
                    <div>{formatDate(video.approved_at)}</div>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <VideoModal videoUrl={selectedVideoUrl} onClose={handleCloseModal} />
    </div>
  )
}

export default VideoReviewsTable
